/**
 * Form01_AuthorizationForReleaseOfInformation.jsx
 * Collection: authorization_release_of_information
 * Redirect: /forms/02-authorization-form  (next form)
 *
 * npm install react-signature-canvas jspdf html2canvas
 */
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  FormShell, Field, SigBlock,
  saveToMongo, downloadPDF, useToast, C,
} from "../shared/formKit";

const API_URL = "/api/forms/authorization-release-of-information";
const NEXT    = "/forms/02-authorization-form";

export default function AuthorizationReleaseOfInformation({
  patientName = "", onSubmit,
}) {
  const history = useHistory();
  const formRef    = useRef(null);
  const sigRef     = useRef(null);
  const [sigEmpty, setSigEmpty] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [showToast, Toast] = useToast();

  const [form, setForm] = useState({
    providingOrg:       "",
    receivingOrg:       "",
    infoDate:           "",
    purpose:            "",
    expirationDate:     "",
    responsibleParty:   "",
    relationship:       "",
    sigDate:            new Date().toISOString().split("T")[0],
  });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const getSig = () => (!sigRef.current||sigRef.current.isEmpty()) ? null : sigRef.current.toDataURL("image/png");

  const buildDoc = () => ({
    formType: "authorization_release_of_information",
    patient: { name: patientName },
    providingOrganization:  form.providingOrg,
    receivingOrganization:  form.receivingOrg,
    infoDate:               form.infoDate || null,
    purpose:                form.purpose,
    expirationDate:         form.expirationDate || null,
    authorization: {
      responsibleParty: form.responsibleParty,
      relationship:     form.relationship,
      sigDate:          form.sigDate || null,
      signatureDataURL: getSig(),
    },
    meta: { submittedAt: new Date().toISOString(), formVersion:"1.0.0" },
  });

  const handleSubmit = async () => {
    if (sigEmpty) { showToast("Please sign before submitting.","error"); return; }
    setSubmitting(true);
    try {
      const doc = buildDoc();
      const result = typeof onSubmit === "function" ? await onSubmit(doc) : await saveToMongo(doc, API_URL);
      if (result?._id) setSavedId(result._id);
      showToast("Saved! Redirecting…","success");
      setTimeout(() => history.push(NEXT), 1800);
    } catch(e) { console.error(e); showToast("Save failed.","error"); }
    finally { setSubmitting(false); }
  };

  const handleDownload = async () => {
    showToast("Generating PDF…","");
    try { await downloadPDF(formRef, `authorization-release-${patientName||"patient"}.pdf`); showToast("Downloaded!","success"); }
    catch(e) { showToast("PDF failed.","error"); }
  };

  return (
    <FormShell title="Authorization for Release of Information"
      subtitle="I hereby authorize the use or disclosure of my individually identified health information as described below."
      dbLabel="MongoDB · authorization_release_of_information"
      patientName={patientName}
      savedId={savedId} formRef={formRef}
      onSubmit={handleSubmit} onDownload={handleDownload} submitting={submitting}>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">1</span>Parties &amp; Purpose</div>
        <div className="wf-grid" style={{gap:16}}>
          <Field label="Persons / Organizations Providing the Information">
            <textarea value={form.providingOrg} onChange={e=>set("providingOrg",e.target.value)}
              placeholder="Name of provider or organization releasing information…" />
          </Field>
          <Field label="Persons / Organizations Receiving the Information">
            <textarea value={form.receivingOrg} onChange={e=>set("receivingOrg",e.target.value)}
              placeholder="Name of person or organization receiving information…" />
          </Field>
          <div className="wf-grid wf-grid-2">
            <Field label="Date of Information">
              <input type="date" value={form.infoDate} onChange={e=>set("infoDate",e.target.value)} />
            </Field>
            <Field label="Authorization Expiration Date">
              <input type="date" value={form.expirationDate} onChange={e=>set("expirationDate",e.target.value)} />
            </Field>
          </div>
          <Field label="Purpose of Information">
            <textarea value={form.purpose} onChange={e=>set("purpose",e.target.value)}
              placeholder="Describe the purpose for which information is being released…" />
          </Field>
        </div>

        <div className="wf-policy" style={{marginTop:20}}>
          <p>I understand that my Healthcare and the payment for my health care will not be affected by my signing this form.</p>
          <p>I understand that I may see a copy of the form after I sign it.</p>
          <p><strong>Note:</strong> You may refuse to sign this authorization. You may not use this form to release information for treatment or payment except when the information to be released is psychotherapy notes or certain research information.</p>
          <p>I understand that if the organization authorized to receive the information is not a health plan or health care provider, the released information may no longer be protected by Federal privacy regulations.</p>
        </div>
      </div>

      <SigBlock sigRef={sigRef} sigEmpty={sigEmpty} setSigEmpty={setSigEmpty}
        responsibleParty={form.responsibleParty} setResponsibleParty={v=>set("responsibleParty",v)}
        relationship={form.relationship} setRelationship={v=>set("relationship",v)}
        sigDate={form.sigDate} setSigDate={v=>set("sigDate",v)} />

      {Toast}
    </FormShell>
  );
}

/*
── Mongoose Schema ────────────────────────────────────────────────────────────
const AuthReleaseSchema = new Schema({
  formType:              { type: String, default: "authorization_release_of_information" },
  patient:               { name: String },
  providingOrganization: String,
  receivingOrganization: String,
  infoDate:              Date,
  purpose:               String,
  expirationDate:        Date,
  authorization: {
    responsibleParty: String,
    relationship:     String,
    sigDate:          Date,
    signatureDataURL: String,
  },
  meta: { submittedAt: Date, formVersion: String },
}, { timestamps: true, collection: "authorization_release_of_information" });
*/
